import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { NewSecretForm, type NewSecretFormOnSubmitInput } from "@/components/secrets/NewSecretForm";
import { SecretLabels } from "@/components/secrets/secret-labels";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateSecret } from "@/queries/secret-queries";
import { formatError } from "@/utils/errors";
import { type SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useState, type FC } from "react";

type Props = {
    scope: SecretScope;
    open: boolean;
    enableSecretContainerRegistry: boolean;
    onClose: () => void;
};

export const NewSecretModal: FC<Props> = ({ scope, open, enableSecretContainerRegistry, onClose }) => {
    const { toast } = useToast();
    const createSecret = useCreateSecret();

    const mode =
        scope.scope.case === "organizationId" ? "organization" : scope.scope.case === "userId" ? "user" : "project";

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const onSubmit = useCallback(
        async ({ secretName, secretValuePlaintext, mount }: NewSecretFormOnSubmitInput) => {
            try {
                await createSecret.mutateAsync({
                    secretName,
                    plaintextValue: secretValuePlaintext,
                    scope,
                    mount,
                });
                onClose();
                toast({
                    title: "Secret successfully created",
                    description: SecretLabels[mode].newSecretModalCreatedToast,
                });
            } catch (e) {
                toast({
                    title: "Sorry, we couldn't create your secret",
                    description: formatError(e),
                });
            }
        },
        [scope, createSecret, onClose, toast, mode],
    );

    const [disabled, setDisabled] = useState<boolean>(true);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="new-secret-form-modal-content"
                data-track-location={TrackLocations.NewSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>New secret</DialogTitle>
                    <DialogDescription>{SecretLabels[mode].newSecretModalDescription}</DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={createSecret.error}
                        data-testid="error-message-secret-creation-failed"
                        className="text-center"
                    />
                    <NewSecretForm
                        formName="new-secret-form"
                        scope={scope}
                        enableSecretContainerRegistry={enableSecretContainerRegistry}
                        onSubmit={onSubmit}
                        onDisabledChange={setDisabled}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="secondary">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="new-secret-form"
                        loading={createSecret.isPending}
                        disabled={createSecret.isPending || disabled}
                        data-testid="add-secret-submit-button"
                        data-tracking-id="confirm-add-secret-new-secret-modal"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
